/**
 * Explanatory tooltip copy for PMEI parameter-table cells. This is the wording counterpart to
 * `PMEICellColorGrade.ts`: where the grade collapses a multi-value cell to a single worst-case
 * color, the tooltip describes every value.
 *
 * Tooltip presence is a superset of warning-grade presence: every graded difference gets a tooltip,
 * but an empty cell with a setpoint also gets an "isn't confirmed/measured yet" message even though
 * it earns no color (nothing recorded means nothing to grade).
 *
 * A multi-value cell with at least one difference splits into two sections: the values differing
 * from plan (each a warning sentence), then every value listed plainly. Single-value cells and the
 * parameter-level empty/unplanned cases stay a single unsectioned line.
 */
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

import {classifyDataParameterDifference} from './PMEICellColorGrade';
import type {MinMaxTypedValue, RangeComparison} from './PMEIParameterRange';

/** The two data-parameter columns that receive tooltips; drives both the verb and the capitalized label. */
export type ColumnKind = 'confirmed' | 'measured';

const COLUMN_LABEL: Record<ColumnKind, string> = {
  confirmed: 'Confirmed',
  measured: 'Measured',
};

/** Heading for the section that lists every recorded value plainly, below the differences section. */
const ALL_VALUES_HEADING = 'All values';

/**
 * A run of tooltip text; `emphasis` marks the dynamic value/name/bound tokens that a difference
 * message bolds so they stand out from the surrounding words. Parameter-level messages
 * (empty/unplanned) and the bare valid-value line use a single plain segment.
 */
export interface TooltipTextSegment {
  text: string;
  emphasis?: boolean;
}

/** A single line of tooltip text, built from plain and emphasized segments. */
export interface TooltipLine {
  segments: Array<TooltipTextSegment>;
}

/** A titled group of tooltip lines — one of the two sections a multi-value differing cell splits into. */
export interface TooltipSection {
  heading: string;
  lines: Array<TooltipLine>;
}

/**
 * A cell tooltip is either a single unsectioned line — the empty/unplanned parameter-level messages
 * and the lone warning sentence for a single-value cell that differs — or two sections for a
 * multi-value cell with at least one difference (differences first, then all values).
 */
export type CellTooltipContent =
  | {kind: 'message'; message: TooltipLine}
  | {kind: 'sections'; sections: Array<TooltipSection>};

/** A run of plain (non-emphasized) tooltip text. */
function plain(text: string): TooltipTextSegment {
  return {text};
}

/** A run of emphasized (bold) tooltip text — the dynamic value/name/bound tokens in a difference. */
function bold(text: string): TooltipTextSegment {
  return {text, emphasis: true};
}

/** A single line of tooltip text from its segments. */
function line(segments: Array<TooltipTextSegment>): TooltipLine {
  return {segments};
}

/** A single unsectioned line — empty/unplanned messages and the single-value difference sentence. */
function message(messageLine: TooltipLine): CellTooltipContent {
  return {kind: 'message', message: messageLine};
}

/** The two-section content for a multi-value cell with at least one difference. */
function sectioned(sections: Array<TooltipSection>): CellTooltipContent {
  return {kind: 'sections', sections};
}

/** Heading for the differences section, e.g. "3 values differing from plan" / "1 value differing from plan". */
function differencesHeading(differingCount: number): string {
  const valueNoun = differingCount === 1 ? 'value' : 'values';
  return `${differingCount} ${valueNoun} differing from plan`;
}

/**
 * Computes the tooltip content for a data-parameter cell (Confirmed or Measured), or null when the
 * cell should have no tooltip. The flow parallels `gradeDimension`:
 * - more than one planned setpoint (plate-map) → null, matching the ungraded grade;
 * - no recorded values but a single setpoint → the empty rule ("… isn't confirmed yet");
 * - recorded values but no setpoint → the unplanned rule ("… was confirmed without a planned setpoint");
 * - recorded values against a single setpoint → a warning line per differing value; null when nothing
 *   differs, a bare sentence when a single-value cell differs, otherwise two sections.
 */
export function computeDataParameterCellTooltip(params: {
  parameterName: string;
  columnKind: ColumnKind;
  cellValues: Array<TypedValue | null>;
  referenceValues: Array<TypedValue | null>;
  minimum?: MinMaxTypedValue;
  maximum?: MinMaxTypedValue;
}): CellTooltipContent | null {
  const {parameterName, columnKind, cellValues, referenceValues, minimum = null, maximum = null} = params;

  const nonEmptyCell = cellValues.filter((v): v is TypedValue => v != null && !isTypedValueEmpty(v));
  const nonEmptyRef = referenceValues.filter((v): v is TypedValue => v != null && !isTypedValueEmpty(v));

  if (nonEmptyRef.length > 1) {
    // Plate-map parameter: no single target to compare against, so the cell is never graded and
    // gets no tooltip either.)
    return null;
  }

  if (nonEmptyCell.length === 0) {
    // An empty cell only merits a message when there is a plan it hasn't been fulfilled against;
    // with nothing planned and nothing recorded there is nothing to say.
    return nonEmptyRef.length === 1 ? message(emptyMessage(parameterName, columnKind)) : null;
  }

  if (nonEmptyRef.length === 0) {
    return message(unplannedMessage(parameterName, columnKind));
  }

  const reference = nonEmptyRef[0];
  const label = COLUMN_LABEL[columnKind];
  // One warning line per value that differs from plan, null for a value that matches.
  const warnings = nonEmptyCell.map((value) => warningLineFor({value, reference, minimum, maximum, label}));
  const differingLines = warnings.filter((w): w is TooltipLine => w != null);

  if (differingLines.length === 0) {
    // Every value is valid: color and tooltip stay in lockstep, so no differences tooltip.
    return null;
  }

  if (nonEmptyCell.length === 1) {
    // A single-value cell that differs shows just its warning sentence, with no section headings.
    return message(differingLines[0]);
  }

  // A multi-value cell with at least one difference: every recorded value as a plain line, for the
  // "All values" section that sits below the differences.
  const allValueLines = nonEmptyCell.map((value) => line([plain(formatTypedValue(value))]));

  return sectioned([
    {heading: differencesHeading(differingLines.length), lines: differingLines},
    {heading: ALL_VALUES_HEADING, lines: allValueLines},
  ]);
}

/**
 * The warning line for one value against the single planned setpoint, or null when it satisfies the
 * plan. Maps the shared `classifyDataParameterDifference` verdict — the same one that drives the
 * color grade — to its wording, so an out-of-range value's line names the breached bound and a plain
 * mismatch names the missed setpoint.
 */
function warningLineFor({
  value,
  reference,
  minimum,
  maximum,
  label,
}: {
  value: TypedValue;
  reference: TypedValue;
  minimum: MinMaxTypedValue;
  maximum: MinMaxTypedValue;
  label: string;
}): TooltipLine | null {
  const difference = classifyDataParameterDifference(value, reference, minimum, maximum);
  if (difference == null) {
    return null;
  }
  if (difference.kind === 'outOfRange') {
    return outOfRangeMessage(label, value, difference.comparison, minimum, maximum);
  }
  return mismatchMessage(label, value, reference);
}

/**
 * "Confirmed value of **5** is below the minimum value of **10**" / "… above the maximum value of …".
 * The value and the bound it breaches are emphasized so the offending numbers stand out.
 */
function outOfRangeMessage(
  label: string,
  value: TypedValue,
  comparison: RangeComparison,
  minimum: MinMaxTypedValue,
  maximum: MinMaxTypedValue
): TooltipLine {
  const direction = comparison.isBelowMinimum ? 'below' : 'above';
  const boundWord = comparison.isBelowMinimum ? 'minimum' : 'maximum';
  const bound = comparison.isBelowMinimum ? minimum : maximum;
  const boundText = bound != null ? formatTypedValue(bound) : '';
  return line([
    plain(`${label} value of `),
    bold(formatTypedValue(value)),
    plain(` is ${direction} the ${boundWord} value of `),
    bold(boundText),
  ]);
}

/**
 * "Confirmed value of **38** does not match the planned setpoint of **37**". The recorded value
 * and the setpoint it misses are emphasized.
 */
function mismatchMessage(label: string, value: TypedValue, reference: TypedValue): TooltipLine {
  return line([
    plain(`${label} value of `),
    bold(formatTypedValue(value)),
    plain(' does not match the planned setpoint of '),
    bold(formatTypedValue(reference)),
  ]);
}

function emptyMessage(parameterName: string, columnKind: ColumnKind): TooltipLine {
  return line([plain(`${parameterName} isn't ${columnKind} yet`)]);
}

function unplannedMessage(parameterName: string, columnKind: ColumnKind): TooltipLine {
  return line([plain(`${parameterName} was ${columnKind} without a planned setpoint`)]);
}
